import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_ENDPOINTS, buildApiUrl } from '../../config/apiConfig';
import AdminHubPageFrame from '../../components/admin/AdminHubPageFrame';
import AdminSectionHeader from '../../components/admin/AdminSectionHeader';
import AdminMetricCard from '../../components/admin/AdminMetricCard';
import AdminStatusBadge from '../../components/admin/AdminStatusBadge';
import AdminDataTable from '../../components/admin/AdminDataTable';
import AdminTableToolbar from '../../components/admin/AdminTableToolbar';
import AdminJsonDetails from '../../components/admin/AdminJsonDetails';
import AdminWarningList from '../../components/admin/AdminWarningList';
import { enforcementModeBadgeTone } from '../../admin/adminHubPresentation';
import { copyTextToClipboard, exportAdminTableAsCsv, exportAdminTableAsJson, normalizeAdminExportRows } from '../../admin/adminExportUtils';
import { filterAdminTableRows, nextSortState, sortAdminTableRows } from '../../admin/adminTableClient';

const ENFORCEMENT_PAIR_KEYS = ['flag', 'value'];

export default function AdminCommercialEnforcementPage() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [searchEnv, setSearchEnv] = useState('');
  const [searchBill, setSearchBill] = useState('');
  const [sortEnv, setSortEnv] = useState({ key: null, dir: null });
  const [sortBill, setSortBill] = useState({ key: null, dir: null });
  const [compactEnv, setCompactEnv] = useState(false);
  const [compactBill, setCompactBill] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(buildApiUrl(API_ENDPOINTS.ADMIN_COMMERCIAL_ENFORCEMENT_READINESS), {
          credentials: 'include',
        });
        if (!res.ok) throw new Error('bad');
        const json = await res.json();
        if (!cancel) {
          setData(json);
          setErr(false);
        }
      } catch {
        if (!cancel) setErr(true);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const runtime = data?.runtime;
  const mode = runtime?.enforcement_mode || 'off';
  const modeTone = enforcementModeBadgeTone(mode);

  const envRows = useMemo(() => {
    const h = data?.envHints || {};
    return [
      {
        __rowKey: 'env_use',
        flag: 'USE_PLAN_LIMITS_RESOLVER_FOR_PAYWALL',
        value: h.USE_PLAN_LIMITS_RESOLVER_FOR_PAYWALL ? 'true' : 'false',
      },
      {
        __rowKey: 'env_mode',
        flag: 'PLAN_RESOLVER_ENFORCEMENT_MODE',
        value: h.PLAN_RESOLVER_ENFORCEMENT_MODE || '—',
      },
    ];
  }, [data]);

  const billingRows = useMemo(() => {
    const b = data?.billingFlagsSubset || {};
    return [
      { __rowKey: 'bf1', flag: 'plan_limits_resolver_paywall', value: String(!!b.plan_limits_resolver_paywall) },
      { __rowKey: 'bf2', flag: 'plan_resolver_enforcement_mode', value: b.plan_resolver_enforcement_mode || '—' },
    ];
  }, [data]);

  const pairCols = useMemo(
    () => [
      { key: 'flag', header: t('adminKit.colSetting') },
      { key: 'value', header: t('adminKit.colValue') },
    ],
    [t]
  );

  const envFiltered = useMemo(
    () => sortAdminTableRows(filterAdminTableRows(envRows, ENFORCEMENT_PAIR_KEYS, searchEnv, {}), sortEnv.key, sortEnv.dir),
    [envRows, searchEnv, sortEnv]
  );
  const billFiltered = useMemo(
    () => sortAdminTableRows(filterAdminTableRows(billingRows, ENFORCEMENT_PAIR_KEYS, searchBill, {}), sortBill.key, sortBill.dir),
    [billingRows, searchBill, sortBill]
  );

  const onSortEnv = useCallback((key) => {
    const n = nextSortState(sortEnv.key, sortEnv.dir, key);
    setSortEnv({ key: n.key, dir: n.dir });
  }, [sortEnv]);
  const onSortBill = useCallback((key) => {
    const n = nextSortState(sortBill.key, sortBill.dir, key);
    setSortBill({ key: n.key, dir: n.dir });
  }, [sortBill]);

  const warnings = useMemo(() => {
    const list = [];
    if (data?.note) {
      list.push({ code: 'NOTE', message: data.note, tone: 'soft' });
    }
    if (String(mode).toLowerCase() === 'enforce_later') {
      list.push({
        code: 'ENFORCE_LATER',
        message: t('adminKit.enforceLaterNotProduction'),
        tone: 'default',
      });
    }
    return list;
  }, [data, mode, t]);

  const crumbs = useMemo(
    () => [
      { label: t('adminHub.navCommercial'), to: '/admin/commercial/plans' },
      { label: t('adminHub.navEnforcementReadiness') },
    ],
    [t]
  );

  return (
    <AdminHubPageFrame
      titleKey="adminHub.enforcementTitle"
      descKey="adminHub.enforcementDesc"
      breadcrumbItems={crumbs}
    >
      <section className="admin-section-card">
        {err ? (
          <div className="admin-state admin-state--error" role="status">
            <p>{t('adminHub.loadError')}</p>
          </div>
        ) : !data ? (
          <div className="admin-state admin-state--loading" role="status">
            <p>{t('adminPage.loading')}</p>
          </div>
        ) : (
          <div className="admin-hub-kit-stack">
            <AdminWarningList items={warnings} />

            <AdminSectionHeader title={t('adminKit.enforcementRuntimeTitle')} subtitle={t('adminKit.enforcementRuntimeSub')} />
            <div className="admin-hub-kit-metric-grid">
              <AdminMetricCard
                label={t('adminKit.metricResolverBridge')}
                value={<AdminStatusBadge tone={runtime?.bridgeEnabled ? 'info' : 'neutral'}>{runtime?.bridgeEnabled ? t('adminKit.yes') : t('adminKit.no')}</AdminStatusBadge>}
                hint={t('adminKit.metricResolverBridgeHint')}
              />
              <AdminMetricCard
                label={t('adminKit.metricEnforcementMode')}
                value={<AdminStatusBadge tone={modeTone}>{mode}</AdminStatusBadge>}
                hint={t('adminKit.metricEnforcementModeHint')}
              />
              <AdminMetricCard
                label={t('adminKit.metricLegacySource')}
                value="billing-access.js + usage.js"
                hint={t('adminKit.metricLegacySourceHint')}
              />
              <AdminMetricCard
                label={t('adminKit.metricResolverSource')}
                value="plan-limits-resolver.js"
                hint={t('adminKit.metricResolverSourceHint')}
              />
            </div>

            <AdminSectionHeader title={t('adminKit.shadowStatsTitle')} level={3} />
            <div className="admin-hub-kit-metric-grid">
              <AdminMetricCard
                label={t('adminKit.metricMismatchCount')}
                value={data.shadowStats?.featureMismatchCount ?? 0}
                hint={t('adminKit.metricMismatchHint')}
              />
              <AdminMetricCard
                label={t('adminKit.metricGuestHints')}
                value={data.shadowStats?.guestHintCount ?? 0}
              />
              <AdminMetricCard
                label={t('adminKit.metricLastMismatch')}
                value={data.shadowStats?.lastMismatchAt || '—'}
              />
              <AdminMetricCard label={t('adminKit.metricLastRoute')} value={data.shadowStats?.lastRoute || '—'} />
            </div>

            <AdminSectionHeader title={t('adminKit.envHintsTitle')} level={3} />
            <AdminTableToolbar
              search={searchEnv}
              onSearchChange={setSearchEnv}
              filteredCount={envFiltered.length}
              totalCount={envRows.length}
              compact={compactEnv}
              onCompactChange={setCompactEnv}
              onExportCsv={() => exportAdminTableAsCsv(envFiltered, ENFORCEMENT_PAIR_KEYS, 'enforcement-env-hints')}
              onExportJson={() => exportAdminTableAsJson(envFiltered, ENFORCEMENT_PAIR_KEYS, 'enforcement-env-hints')}
              onCopyFilteredJson={() => copyTextToClipboard(JSON.stringify(normalizeAdminExportRows(envFiltered, ENFORCEMENT_PAIR_KEYS)))}
            />
            <AdminDataTable
              columns={pairCols}
              rows={envFiltered}
              emptyMessage={searchEnv.trim() ? t('adminExport.noResults') : t('adminKit.tableEmpty')}
              emptyVariant={searchEnv.trim() ? 'noResults' : 'default'}
              compact={compactEnv}
              sortable
              sortKey={sortEnv.key}
              sortDir={sortEnv.dir}
              onSortChange={onSortEnv}
              copyRowKeys={ENFORCEMENT_PAIR_KEYS}
            />

            <AdminSectionHeader title={t('adminKit.systemBillingFlagsTitle')} level={3} />
            <AdminTableToolbar
              search={searchBill}
              onSearchChange={setSearchBill}
              filteredCount={billFiltered.length}
              totalCount={billingRows.length}
              compact={compactBill}
              onCompactChange={setCompactBill}
              onExportCsv={() => exportAdminTableAsCsv(billFiltered, ENFORCEMENT_PAIR_KEYS, 'enforcement-billing-flags')}
              onExportJson={() => exportAdminTableAsJson(billFiltered, ENFORCEMENT_PAIR_KEYS, 'enforcement-billing-flags')}
              onCopyFilteredJson={() => copyTextToClipboard(JSON.stringify(normalizeAdminExportRows(billFiltered, ENFORCEMENT_PAIR_KEYS)))}
            />
            <AdminDataTable
              columns={pairCols}
              rows={billFiltered}
              emptyMessage={searchBill.trim() ? t('adminExport.noResults') : t('adminKit.tableEmpty')}
              emptyVariant={searchBill.trim() ? 'noResults' : 'default'}
              compact={compactBill}
              sortable
              sortKey={sortBill.key}
              sortDir={sortBill.dir}
              onSortChange={onSortBill}
              copyRowKeys={ENFORCEMENT_PAIR_KEYS}
            />

            <AdminJsonDetails title={t('adminKit.rawJsonToggle')} data={data} />
          </div>
        )}
      </section>
    </AdminHubPageFrame>
  );
}
